import type { AllowedActionable } from 'pkg/api/models/allowed_actionable';
import type { EventFlags, RSVPInterval } from 'pkg/api/models/event';

export enum SessionStatus {
	Open = 'open',
	Staged = 'staged',
	Discarded = 'discarded',
	Confirmed = 'confirmed',
	Reverted = 'reverted',
	// The read is still running as a job and the session has no rows yet. A
	// schedule at the line limit takes minutes, so this is where the flow
	// spends most of an import's wall clock.
	Reading = 'reading',
	// The read produced nothing. Terminal: the club starts again rather than
	// repairing a working set that was never built.
	Failed = 'failed',
}

export enum CauseSeverity {
	// A verified repair is on the card for the user to tap.
	Warning = 'warning',
	// The repair needs an answer from the user before it can be applied.
	Question = 'question',
	// Leaving the rows out is the only option.
	Error = 'error',
}

export enum RefusalKind {
	// The read looked at the line and would not take it. The user's data.
	Line = 'line',
	// Nobody looked, because the chunk's request failed. Ours, not theirs.
	Failed = 'failed',
}

export interface Refusal {
	line: number;
	text: string;
	reason: string;
	kind: RefusalKind;
}

export interface SessionRow {
	id: number;
	line: number;
	cells: Record<string, string>;
	skipped: boolean;
}

export interface CauseAction {
	key: string;
	label: string;
}

export interface Cause {
	code: string;
	column?: string;
	// The cell the cause is about, for the codes where the answer depends on it.
	value?: string;
	question: string;
	detail?: string;
	lines: number[];
	actions: CauseAction[];
	// The values a set_value answer may carry, for a cell the source never filled.
	suggestions?: string[];
	severity: CauseSeverity;
}

export interface RowIssue {
	line: number;
	column?: string;
	code: string;
	reason: string;
	cells: Record<string, string>;
}

export interface CellChange {
	column: string;
	from: string;
	to: string;
}

export interface PreviewRow {
	line: number;
	title: string;
	type: string;
	date: string;
	startTime: string;
	location?: string;
	duplicate: boolean;
	eventId?: number;
	// The section's series this row is created through, when it is not an event of its own.
	seriesId?: number;
	changes: CellChange[];
}

export interface PreviewSeries {
	id: number;
	rRuleString: string;
	timezone: string;
	count: number;
}

export interface GroupSection {
	group: string;
	groupId: number;
	// Blank for a group with no sport, which has no game setup to offer.
	sportType?: string;
	new: number;
	duplicates: number;
	series: PreviewSeries[];
	rows: PreviewRow[];
}

export interface PrepareResult {
	importId: string;
	total: number;
	new: number;
	duplicates: number;
	sections: GroupSection[];
	issues: RowIssue[];
	causes: Cause[];
}

export interface EventSettings {
	groupId: number;
	type: string;
	flags: EventFlags[];
	// Optional on the wire — the importer only overrides what an entry carries
	// — but the settings step fills all four in on every entry.
	meetBeforeMinutes?: number;
	hideParticipants?: boolean;
	isPrivate?: boolean;
	isPublic?: boolean;
	// Both audiences travel together, the way the event form and the schedule
	// publish send them; inviteUsersAsPending is add-as-attending versus
	// invite-as-pending.
	inviteUsersToEvent: boolean;
	inviteAdminAndStaffToEvent: boolean;
	inviteUsersAsPending: boolean;
	// A zero count is the interval switched off.
	rsvpIntervalCount: number;
	rsvpInterval: RSVPInterval;
	inviteIntervalCount: number;
	inviteInterval: RSVPInterval;
	reminderIntervalCount: number;
	reminderInterval: RSVPInterval;
}

export interface GameSettings {
	groupId: number;
	format?: string;
	periods?: number;
	periodLength?: number;
	surface?: string;
	indoorOutdoor?: string;
}

export interface AnsweredCause {
	code: string;
	column?: string;
	value?: string;
	// The rows the cause covered when it was answered: an answer settles those
	// rows, not the question, so the same rule broken later asks again.
	lines?: number[];
	action?: string;
	answer: string;
	answeredAt: number;
}

export interface SessionState {
	importId: string;
	status: SessionStatus;
	rows: SessionRow[];
	answeredCauses: AnsweredCause[];
	preview: PrepareResult;
}

export interface ReadResponse {
	refusals: Refusal[];
	notes: string;
	session: SessionState;
	failure?: ReadFailure;
	// The file being read, set only while the session is reading.
	upload?: ReadUpload;
}

export interface ReadUpload {
	filename: string;
	bytes: number;
}

export enum ReadFailure {
	NoEvents = 'no_events',
	Unavailable = 'unavailable',
}

export interface ScheduledRead {
	importId: string;
}

export interface Question {
	text: string;
	options: string[];
}

export interface ReviseResponse {
	question: Question | null;
	notes: string;
	session: SessionState | null;
}

export interface Import extends AllowedActionable {
	// The session's own id, which is what a collection is keyed by. Every
	// endpoint takes importId instead.
	id: number;
	importId: string;
	name: string;
	groupId: number;
	// The team the rows went to, shown under the name.
	groupName: string;
	createdAt: number;
	// Nobody has confirmed this one yet: it created nothing, and its row is
	// the way back into the flow.
	inProgress: boolean;
}

export interface Status {
	importId: string;
	status: string;
	total: number;
	created: number;
	updated: number;
	unchanged: number;
	rejected: number;
	errors?: string[];
	warnings?: string[];
	error?: string;
	// Where the last deletion of the import's events got to, absent when none
	// was ever asked for.
	revert?: string;
}

export enum ImportStatus {
	AwaitingUpload = 'awaiting_upload',
	AwaitingReview = 'awaiting_review',
	Pending = 'pending',
	Running = 'running',
	Completed = 'completed',
	Failed = 'failed',
}

export interface CreatedRow {
	eventId: number;
	title: string;
	type: string;
	date: string;
	startTime: string;
	location?: string;
}

export interface CreatedSection {
	group: string;
	groupId: number;
	rows: CreatedRow[];
}

export interface CreatedSections {
	sections: CreatedSection[];
}

export interface RejectedRow {
	cells: Record<string, string>;
	reason: string;
}

export interface RejectedRows {
	rows: RejectedRow[];
}
